/**
 * CORE-495 — browser tab titles.
 *
 * Every `<Stack.Screen>` in App.tsx used to carry a hardcoded English `title`
 * («Chats — Wisdom Conversations», «Practices», «My Profile», …), and that
 * string is the ONLY thing the web build does with `options.title`: the root
 * navigator runs `headerShown: false`, so nothing renders it on screen — it
 * goes straight into `document.title` through the `documentTitle.formatter` on
 * NavigationContainer. A Russian-speaking user therefore got a Russian page
 * inside an English browser tab.
 *
 * The titles now live here, keyed by ROUTE NAME and resolved through i18next,
 * so the tab follows the user's language like the rest of the UI (CORE-573
 * made Russian the default; this makes the tab honour it).
 *
 * PURE by design — `t` is injected rather than imported — so the mapping unit
 * tests in the node/ts-jest env without dragging expo-localization and the
 * storage layer into the runner.
 *
 * A screen that knows its own title still wins: PracticeDetailScreen and
 * useLiveDetailData call `navigation.setOptions({ title })` with the practice
 * or session name, and `documentTitle` passes any such runtime title straight
 * through. The table is the fallback for the other ~60 routes, which have no
 * content-specific name to show.
 */

/** Suffix appended to every page title. Mirrors `expo.web.name` in app.json. */
export const BRAND = 'Agapella';

/**
 * Route name → i18next key under the `pageTitle` group of the `common`
 * namespace. Keep in sync with the `<Stack.Screen name="…">` list in App.tsx —
 * `routeTitles-CORE495.test.ts` fails the build if the two drift apart.
 */
export const ROUTE_TITLE_KEYS: Record<string, string> = {
  Home: 'pageTitle.home',
  News: 'pageTitle.news',
  NewsArticle: 'pageTitle.newsArticle',
  Landing: 'pageTitle.landing',
  ReferralLanding: 'pageTitle.referralLanding',
  Login: 'pageTitle.login',
  Register: 'pageTitle.register',
  ConfirmEmail: 'pageTitle.confirmEmail',
  ForgotPassword: 'pageTitle.forgotPassword',
  ResetPassword: 'pageTitle.resetPassword',
  Dashboard: 'pageTitle.dashboard',
  Me: 'pageTitle.me',
  Masters: 'pageTitle.masters',
  MasterDetail: 'pageTitle.masterDetail',
  MasterSubscriptions: 'pageTitle.masterSubscriptions',
  ProgramPurchase: 'pageTitle.programPurchase',
  ProgramPage: 'pageTitle.programPage',
  AgapeCenter: 'pageTitle.agapeCenter',
  Lives: 'pageTitle.lives',
  EventsCalendar: 'pageTitle.eventsCalendar',
  EventsAll: 'pageTitle.eventsAll',
  EventsFilters: 'pageTitle.eventsFilters',
  EventDetail: 'pageTitle.eventDetail',
  CreateEvent: 'pageTitle.createEvent',
  EventParticipants: 'pageTitle.eventParticipants',
  Schedule: 'pageTitle.schedule',
  Upcoming: 'pageTitle.upcoming',
  Practices: 'pageTitle.practices',
  PracticeDetail: 'pageTitle.practiceDetail',
  UploadPractice: 'pageTitle.uploadPractice',
  MyCollection: 'pageTitle.myCollection',
  Settings: 'pageTitle.settings',
  LiveDetail: 'pageTitle.liveDetail',
  LiveBroadcast: 'pageTitle.liveBroadcast',
  Chats: 'pageTitle.chats',
  ChatThread: 'pageTitle.chatThread',
  Community: 'pageTitle.community',
  PublicUserProfile: 'pageTitle.publicUserProfile',
  PublicUserPost: 'pageTitle.publicUserPost',
  // CORE-966 — /s/:token opens the shared card's «Путь Света».
  GamificationShare: 'pageTitle.gamificationShare',
  CircleChat: 'pageTitle.circleChat',
  About: 'pageTitle.about',
  Download: 'pageTitle.download',
  AboutPlatform: 'pageTitle.aboutPlatform',
  Environment: 'pageTitle.environment',
  // CORE-901 — скрытое QA-превью состояний геймификации.
  GamificationPreview: 'pageTitle.gamificationPreview',
  Support: 'pageTitle.support',
  Contact: 'pageTitle.contact',
  NotificationSettings: 'pageTitle.notificationSettings',
  SecuritySettings: 'pageTitle.securitySettings',
  BlockedUsers: 'pageTitle.blockedUsers',
  MyCalendar: 'pageTitle.myCalendar',
  MyLibrary: 'pageTitle.myLibrary',
  MemoryFacts: 'pageTitle.memoryFacts',
  Fund: 'pageTitle.fund',
  Finance: 'pageTitle.finance',
  FinanceDocuments: 'pageTitle.financeDocuments',
  AgapellaSubscription: 'pageTitle.agapellaSubscription',
  MasterSales: 'pageTitle.masterSales',
  MasterAnalytics: 'pageTitle.masterAnalytics',
  Notifications: 'pageTitle.notifications',
  FundDistribution: 'pageTitle.fundDistribution',
  HowItWorks: 'pageTitle.howItWorks',
  FundTransparency: 'pageTitle.fundTransparency',
  ReferralCredits: 'pageTitle.referralCredits',
  PayoutDetails: 'pageTitle.payoutDetails',
  FundOnboarding: 'pageTitle.fundOnboarding',
  MasterWallet: 'pageTitle.masterWallet',
  Docs: 'pageTitle.docs',
  LegalDoc: 'pageTitle.legalDoc',
  CustDev: 'pageTitle.custDev',
  SoundLibrary: 'pageTitle.soundLibrary',
  PracticeRooms: 'pageTitle.practiceRooms',
  CreateLiveSession: 'pageTitle.createLiveSession',
  LiveSessionEntry: 'pageTitle.liveSessionEntry',
  LiveRoomSession: 'pageTitle.liveRoomSession',
  RoomAnnouncement: 'pageTitle.roomAnnouncement',
  AgapePractice: 'pageTitle.agapePractice',
  EventThreadScreen: 'pageTitle.eventThread',
  // CORE-1114 (B-6) — экран нативный, вкладки у него нет; заголовок тот же «Обсуждение».
  PracticeDiscussion: 'pageTitle.eventThread',
  Feed: 'pageTitle.feed',
  GuestRoom: 'pageTitle.guestRoom',
};

/** Shown before the first navigation resolves, and for any route we don't know. */
export const DEFAULT_TITLE_KEY = 'pageTitle.default';

interface TitleOptions {
  title?: string;
}

interface TitleRoute {
  name?: string;
}

/**
 * The `documentTitle.formatter` for NavigationContainer.
 *
 * Precedence: a title the screen set at runtime (a practice name, a session
 * name) → the localized name of the route → the localized product tagline.
 */
export function documentTitle(
  t: (key: string) => string,
  options?: TitleOptions | null,
  route?: TitleRoute | null,
): string {
  const runtime = options?.title?.trim();
  if (runtime) return `${runtime} — ${BRAND}`;

  const key = route?.name ? ROUTE_TITLE_KEYS[route.name] : undefined;
  if (key) return `${t(key)} — ${BRAND}`;

  return t(DEFAULT_TITLE_KEY);
}
